import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  advanceFollowUp,
  startRealtimeSession,
  cancelRealtimeSession,
} from '../../../services/Interviews/chatbotService';
import AudioRecorder from '../elements/AudioRecorder';
import {
  ArrowPathIcon,
  StopCircleIcon,
  MicrophoneIcon,
} from '@heroicons/react/24/solid';

export default function FollowUpStep({
  parentQuestion,
  sessionId,
  initialChainData,
  liveAvatar,
  questionNumber,
  totalQuestions,
  onChainComplete,
}) {
  const [chainData, setChainData] = useState(initialChainData);
  // True once a LiveAvatar realtime session was started for this chain.
  // Flips back to false permanently if the avatar errors out.
  const [avatarActive, setAvatarActive] = useState(false);
  // True while POST /realtime_session/ is in flight — the surface shows a
  // loader instead of flashing the text card before the avatar appears.
  const [realtimePending, setRealtimePending] = useState(false);
  const [phase, setPhase] = useState('asking'); // 'asking' | 'recording' | 'thinking' | 'error'
  const [timer, setTimer] = useState(180);
  const [isStartingRecording, setIsStartingRecording] = useState(false);

  const audioRecorderRef = useRef(null);
  const timerIntervalRef = useRef(null);
  const recordingStartTimeRef = useRef(null);
  const submittedRef = useRef(false);
  const inFlightRef = useRef(false);
  const retryBlobRef = useRef(null);
  const retryDurationRef = useRef(null);
  const completedRef = useRef(false);
  const speakRequestedRef = useRef(false);

  const timeAllotted = 3 * 60;

  const currentQuestion = chainData?.next_question || null;
  const liveAvatarStatus = liveAvatar?.status;
  const showVideo =
    avatarActive &&
    (liveAvatarStatus === 'connecting' ||
      liveAvatarStatus === 'ready' ||
      liveAvatarStatus === 'speaking');

  const liveAvatarStart = liveAvatar?.start;
  const liveAvatarSpeak = liveAvatar?.speak;
  const liveAvatarStop = liveAvatar?.stop;

  // The prefetched init deliberately skips the realtime session (prepare
  // mode) so the avatar doesn't talk to nobody while the candidate reviews
  // their transcript. Start it now that they are actually here: connect the
  // LiveKit room + websocket, then speak the first follow-up question.
  useEffect(() => {
    if (speakRequestedRef.current) return;
    if (!currentQuestion) return;
    speakRequestedRef.current = true;
    let cancelled = false;
    setRealtimePending(true);
    startRealtimeSession({
      params: {
        sessionId,
        interviewQuestionUuid: parentQuestion.interview_question_uuid,
      },
    }).then((res) => {
      if (cancelled) {
        // Component left before the session arrived — don't leak it.
        if (res?.realtime?.session_token) {
          cancelRealtimeSession({
            params: { sessionToken: res.realtime.session_token },
          });
        }
        return;
      }
      setRealtimePending(false);
      // res null / res.realtime null -> stay in text mode.
      if (res?.realtime && liveAvatarStart) {
        liveAvatarStart(res.realtime);
        if (liveAvatarSpeak) liveAvatarSpeak(res.speech?.chunks);
        setAvatarActive(true);
      }
    });
    return () => {
      cancelled = true;
      // StrictMode remount (or a rare dep churn) must RETRY, not permanently
      // kill the avatar path: the cancelled invocation cleans up its own
      // session above, and resetting the guard lets the next invocation
      // start fresh. The hook's own startedRef prevents double-starts.
      speakRequestedRef.current = false;
    };
    // Mount-only on purpose: currentQuestion changes on every advance and
    // must not cancel a still-pending session start.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The avatar died (LiveKit / websocket error): fall back to the text card
  // permanently and keep the chain going. stop() (idempotent) tears down the
  // room/ws/keep-alive and cancels the metered backend session so nothing
  // keeps billing invisibly.
  useEffect(() => {
    if (avatarActive && liveAvatarStatus === 'error') {
      setAvatarActive(false);
      if (liveAvatarStop) liveAvatarStop();
    }
  }, [avatarActive, liveAvatarStatus, liveAvatarStop]);

  // Chain already complete (e.g. resumed session) -> hand back immediately.
  useEffect(() => {
    if (!currentQuestion && !completedRef.current) {
      completedRef.current = true;
      if (liveAvatarStop) liveAvatarStop();
      onChainComplete();
    }
  }, [currentQuestion, liveAvatarStop, onChainComplete]);

  useEffect(() => {
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (phase === 'recording') {
      timerIntervalRef.current = setInterval(() => {
        setTimer((prevTimer) => {
          if (prevTimer <= 1) {
            clearInterval(timerIntervalRef.current);
            triggerStopAndSubmitAudio();
            return 0;
          }
          return prevTimer - 1;
        });
      }, 1000);
    } else {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    }
    return () => clearInterval(timerIntervalRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  const submitAnswer = useCallback(
    async (audioBlob, duration) => {
      if (inFlightRef.current || !currentQuestion) return;
      inFlightRef.current = true;
      setPhase('thinking');

      try {
        const snapshot = await advanceFollowUp({
          params: {
            sessionId,
            interviewQuestionUuid: parentQuestion.interview_question_uuid,
            sequence: currentQuestion.sequence,
            recordedBlob: audioBlob,
            duration,
          },
        });
        retryBlobRef.current = null;
        retryDurationRef.current = null;
        submittedRef.current = false;

        // Speech chunks arrive only when this advance newly generated a
        // follow-up. speak() is a no-op in text mode (avatar not started).
        if (snapshot?.speech?.chunks && avatarActive && liveAvatarSpeak) {
          liveAvatarSpeak(snapshot.speech.chunks);
        }

        if (snapshot?.next_question) {
          setChainData(snapshot);
          setTimer(timeAllotted);
          setPhase('asking');
        } else if (!completedRef.current) {
          completedRef.current = true;
          if (liveAvatarStop) liveAvatarStop();
          onChainComplete();
        }
      } catch (error) {
        console.error('Error submitting follow-up answer:', error);
        setPhase('error');
      } finally {
        inFlightRef.current = false;
      }
    },
    [
      avatarActive,
      currentQuestion,
      liveAvatarSpeak,
      liveAvatarStop,
      onChainComplete,
      parentQuestion,
      sessionId,
      timeAllotted,
    ],
  );

  const handleAudioRecordingComplete = async (audioBlob) => {
    if (!audioBlob) {
      // Empty recording (e.g. instant start/stop) — reset so the
      // candidate can record again instead of getting stuck.
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
      submittedRef.current = false;
      setTimer(timeAllotted);
      setPhase('asking');
      return;
    }
    if (submittedRef.current) {
      return;
    }
    submittedRef.current = true;

    let duration = 0;
    if (recordingStartTimeRef.current) {
      duration = Math.floor(
        (Date.now() - recordingStartTimeRef.current) / 1000,
      );
    }

    // Keep the blob so a failed submit can be retried without re-recording.
    retryBlobRef.current = audioBlob;
    retryDurationRef.current = duration;

    await submitAnswer(audioBlob, duration);
  };

  const handleAudioRecordingError = (error) => {
    console.error('Audio Recording Error:', error);
    alert(
      `Could not start or continue recording: ${
        error.message || 'Please check microphone permissions and try again.'
      }`,
    );
    setIsStartingRecording(false);
    setPhase('asking');
  };

  const triggerStartAudioRecording = async () => {
    if (!audioRecorderRef.current || phase !== 'asking') return;

    submittedRef.current = false;
    retryBlobRef.current = null;
    retryDurationRef.current = null;

    // A guaranteed user gesture — resume audio if autoplay was blocked.
    if (liveAvatar?.needsAudioStart && liveAvatar.resumeAudio) {
      liveAvatar.resumeAudio();
    }

    setIsStartingRecording(true);
    const success = await audioRecorderRef.current.startRecording();
    if (success) {
      recordingStartTimeRef.current = Date.now();
      setTimer(timeAllotted);
      setPhase('recording');
    }
    setIsStartingRecording(false);
  };

  const triggerStopAndSubmitAudio = () => {
    if (!audioRecorderRef.current || inFlightRef.current) return;
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }
    audioRecorderRef.current.stopRecording();
  };

  const handleRetrySubmit = () => {
    if (!retryBlobRef.current) {
      setPhase('asking');
      return;
    }
    submitAnswer(retryBlobRef.current, retryDurationRef.current);
  };

  const formatTime = (timeInSeconds) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = timeInSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds
      .toString()
      .padStart(2, '0')}`;
  };

  if (!currentQuestion) {
    return (
      <p className="text-center dark:text-gray-300">Loading follow-up...</p>
    );
  }

  return (
    <div className="flex flex-col items-center gap-4 w-full max-w-3xl p-4 md:p-6">
      <AudioRecorder
        ref={audioRecorderRef}
        onRecordingComplete={handleAudioRecordingComplete}
        onRecordingError={handleAudioRecordingError}
      />

      <div className="text-center w-full">
        <h1 className="text-lg text-left max-w-1/2 text-wrap h-auto overflow-auto font-semibold text-gray-500 dark:text-gray-300">
          Question {questionNumber}/{totalQuestions} — Follow-up{' '}
          {currentQuestion.sequence}
        </h1>
      </div>

      <div className="w-full aspect-[16/9] bg-gray-900 rounded-lg overflow-hidden shadow-lg relative">
        {realtimePending && !showVideo ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-gray-900 text-white">
            <ArrowPathIcon className="w-8 h-8 animate-spin" />
            <p className="text-sm">Connecting to interviewer...</p>
          </div>
        ) : showVideo ? (
          <>
            <video
              ref={liveAvatar.videoRef}
              autoPlay
              playsInline
              className="w-full h-full object-cover"
            />
            {liveAvatarStatus !== 'ready' &&
              liveAvatarStatus !== 'speaking' && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-gray-900 bg-opacity-70 text-white">
                  <ArrowPathIcon className="w-8 h-8 animate-spin" />
                  <p className="text-sm">Connecting to interviewer...</p>
                </div>
              )}
            {liveAvatar?.needsAudioStart && (
              <button
                type="button"
                onClick={() => liveAvatar.resumeAudio && liveAvatar.resumeAudio()}
                className="absolute bottom-2 right-2 md:bottom-4 md:right-4 bg-black bg-opacity-70 hover:bg-opacity-90 text-white px-3 py-2 rounded-md text-sm font-semibold"
              >
                🔊 Enable sound
              </button>
            )}
          </>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-white bg-gray-700 p-4">
            <p className="text-sm uppercase tracking-wide text-gray-300 mb-3">
              Follow-up question
            </p>
            <p className="text-lg md:text-xl text-center font-semibold">
              {currentQuestion.question_text}
            </p>
          </div>
        )}
        <div className="absolute top-2 left-2 md:top-4 md:left-4 bg-black bg-opacity-60 text-white px-2 py-1 md:px-3 rounded-md text-base font-semibold">
          Time Allotted: {formatTime(timeAllotted)}
        </div>
        {phase === 'recording' && (
          <div className="absolute top-2 right-2 md:top-4 md:right-4 bg-black bg-opacity-60 text-white px-2 py-1 md:px-3 rounded-md text-base font-mono bg-red-500">
            {formatTime(timer)}
          </div>
        )}
        {phase === 'recording' && (
          <div className="absolute bottom-2 left-2 md:bottom-4 md:left-4 flex items-center gap-2 bg-red-600 text-white px-2 py-1 md:px-3 rounded-md text-base font-semibold">
            <MicrophoneIcon className="w-4 h-4 md:w-5 md:h-5 animate-pulse" />
            Recording Audio...
          </div>
        )}
      </div>

      {(showVideo || realtimePending) && (
        <div className="w-full">
          <p className="text-base text-gray-800 dark:text-gray-200 break-words whitespace-pre-line">
            {currentQuestion.question_text}
          </p>
        </div>
      )}

      <div className="w-full flex flex-row items-center gap-3">
        {phase === 'asking' && (
          <button
            onClick={triggerStartAudioRecording}
            disabled={isStartingRecording}
            className="w-full max-w-64 bg-blue-600 text-white px-6 py-3 rounded-lg text-sm md:text-base font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <MicrophoneIcon className="w-5 h-5" />
            {isStartingRecording ? 'Please wait...' : 'Start Recording Answer'}
          </button>
        )}

        {phase === 'recording' && (
          <button
            onClick={triggerStopAndSubmitAudio}
            className="w-full max-w-64 bg-red-600 text-white px-6 py-3 rounded-lg text-sm md:text-base font-medium hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <StopCircleIcon className="w-5 h-5" />
            Stop & Submit Answer
          </button>
        )}

        {phase === 'thinking' && (
          <div className="w-full flex flex-row items-center gap-2 text-gray-600 dark:text-gray-400">
            <ArrowPathIcon className="w-5 h-5 animate-spin" />
            <p className="text-sm md:text-base font-medium">
              Interviewer is thinking...
            </p>
          </div>
        )}

        {phase === 'error' && (
          <div className="flex flex-col sm:flex-row gap-3 w-full items-center">
            <p className="text-xs md:text-sm text-red-500">
              Failed to submit your answer. Your recording is saved — please
              try again.
            </p>
            <button
              onClick={handleRetrySubmit}
              className="w-full sm:w-auto bg-blue-600 text-white px-6 py-3 rounded-lg text-sm md:text-base font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <ArrowPathIcon className="w-5 h-5" />
              Retry
            </button>
          </div>
        )}

        {(phase === 'asking' || phase === 'recording') && (
          <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">
            One attempt only — stopping submits your answer.
          </p>
        )}
      </div>
    </div>
  );
}
